/**
 * /design/icons — the living catalog of the domain icon registry.
 *
 * Unlisted QA/reference page: every domain glyph with its registry id and the
 * Arabic + English labels it ships with, grouped the way the product thinks
 * (types / metrics / amenities / utilities). If a concept renders differently
 * anywhere in the app, this page is the source of truth for what it SHOULD be.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DomainIcon from '../../../components/base/icons/domain/DomainIcon';
import { DOMAIN_ICON_IDS, type DomainIconId } from '../../../components/base/icons/domain/registry';
import { SEARCH_TYPE_CATALOG } from '../../../config/propertyTypeCatalog';
import { AMENITIES_LIST, UTILITIES_LIST } from '../../../config/propertyFormOptions';

interface CatalogEntry {
  id: string;
  labelAr: string;
  labelEn: string;
}

interface CatalogGroup {
  key: string;
  titleAr: string;
  titleEn: string;
  entries: CatalogEntry[];
}

const METRIC_ENTRIES: CatalogEntry[] = [
  { id: 'bedroom', labelAr: 'غرف النوم', labelEn: 'Bedrooms' },
  { id: 'bathroom', labelAr: 'دورات المياه', labelEn: 'Bathrooms' },
  { id: 'area', labelAr: 'المساحة (م²)', labelEn: 'Area (m²)' },
  { id: 'parking', labelAr: 'مواقف السيارات', labelEn: 'Parking' },
  { id: 'floors', labelAr: 'الأدوار', labelEn: 'Floors' },
  { id: 'price-sqm', labelAr: 'سعر المتر', labelEn: 'Price per m²' },
];

const LEGACY_AMENITY_ENTRIES: CatalogEntry[] = [
  { id: 'furnished', labelAr: 'مفروش', labelEn: 'Furnished' },
  { id: 'kitchen', labelAr: 'مطبخ', labelEn: 'Kitchen' },
  { id: 'laundry', labelAr: 'غرفة غسيل', labelEn: 'Laundry' },
  { id: 'walk_in_closet', labelAr: 'غرفة ملابس', labelEn: 'Walk-in closet' },
  { id: 'private_entrance', labelAr: 'مدخل خاص', labelEn: 'Private entrance' },
  { id: 'study_room', labelAr: 'غرفة دراسة', labelEn: 'Study room' },
  { id: 'fireplace', labelAr: 'مدفأة', labelEn: 'Fireplace' },
  { id: 'sauna', labelAr: 'ساونا', labelEn: 'Sauna' },
  { id: 'jacuzzi', labelAr: 'جاكوزي', labelEn: 'Jacuzzi' },
  { id: 'lobby', labelAr: 'ردهة', labelEn: 'Lobby' },
  { id: 'cctv', labelAr: 'كاميرات مراقبة', labelEn: 'CCTV' },
  { id: 'guard', labelAr: 'حارس أمن', labelEn: 'Guard' },
  { id: 'fire_alarm', labelAr: 'إنذار حريق', labelEn: 'Fire alarm' },
  { id: 'generator', labelAr: 'مولد احتياطي', labelEn: 'Backup generator' },
  { id: 'double_glazing', labelAr: 'زجاج مزدوج', labelEn: 'Double glazing' },
  { id: 'soundproofing', labelAr: 'عزل صوتي', labelEn: 'Soundproofing' },
  { id: 'wheelchair', labelAr: 'وصول لذوي الاحتياجات', labelEn: 'Wheelchair access' },
  { id: 'amenity', labelAr: 'ميزة (افتراضي)', labelEn: 'Amenity (fallback)' },
  { id: 'utility', labelAr: 'خدمة (افتراضي)', labelEn: 'Utility (fallback)' },
];

export default function IconGalleryPage() {
  const { t, i18n } = useTranslation(['search']);
  const isRTL = i18n.dir() === 'rtl';
  const [filter, setFilter] = useState('');

  const groups = useMemo<CatalogGroup[]>(() => {
    const typeEntries: CatalogEntry[] = SEARCH_TYPE_CATALOG.map((o) => ({
      id: o.iconId,
      labelAr: t(`search.filters.propertyTypeOptions.${o.enum}`, { lng: 'ar', defaultValue: o.id }),
      labelEn: t(`search.filters.propertyTypeOptions.${o.enum}`, { lng: 'en', defaultValue: o.id }),
    }));
    // *_land variants share one glyph — collapse duplicates for display
    const seenTypeIds = new Set<string>();
    const dedupedTypes = typeEntries.filter((e) => !seenTypeIds.has(e.id) && seenTypeIds.add(e.id));

    return [
      { key: 'types', titleAr: 'أنواع العقارات', titleEn: 'Property types', entries: dedupedTypes },
      { key: 'metrics', titleAr: 'المقاييس', titleEn: 'Metrics', entries: METRIC_ENTRIES },
      {
        key: 'amenities',
        titleAr: 'المزايا',
        titleEn: 'Amenities',
        entries: AMENITIES_LIST.map((a) => ({ id: a.id, labelAr: a.labelAr, labelEn: a.label })),
      },
      {
        key: 'utilities',
        titleAr: 'الخدمات',
        titleEn: 'Utilities',
        entries: UTILITIES_LIST.map((u) => ({ id: u.id, labelAr: u.labelAr, labelEn: u.label })),
      },
      { key: 'legacy', titleAr: 'مزايا قديمة + الافتراضي', titleEn: 'Legacy amenities + fallbacks', entries: LEGACY_AMENITY_ENTRIES },
    ];
  }, [t]);

  const totalInRegistry = DOMAIN_ICON_IDS.length;
  const shown = groups.flatMap((g) => g.entries.map((e) => e.id));
  const uncatalogued = DOMAIN_ICON_IDS.filter((id) => !shown.includes(id));

  const match = (e: CatalogEntry) =>
    !filter.trim() ||
    [e.id, e.labelAr, e.labelEn].some((v) => v.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <main dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-cream-soft px-4 py-10 text-ink sm:px-8">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#8C6A0B]">Safaqat 24 · Design</p>
          <h1 className="mt-1 text-3xl font-black">{isRTL ? 'أيقونات النظام' : 'Domain icons'}</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-muted">
            {isRTL
              ? `المرجع الموحّد لأيقونات المنصة (${totalInRegistry} أيقونة). كل مفهوم عقاري له أيقونة واحدة واسم عربي واحد أينما ظهر.`
              : `The unified reference for the platform's icons (${totalInRegistry} glyphs). One icon and one Arabic term per concept, everywhere.`}
          </p>
          <input
            type="search"
            name="icon-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={isRTL ? 'تصفية بالاسم أو المعرّف…' : 'Filter by name or id…'}
            aria-label={isRTL ? 'تصفية الأيقونات' : 'Filter icons'}
            autoComplete="off"
            className="mt-4 w-full max-w-sm rounded-xl border border-[var(--pv-border-soft)] bg-white px-4 py-2.5 text-sm outline-none focus-visible:border-mq-primary/50 focus-visible:ring-2 focus-visible:ring-mq-primary/20"
          />
        </header>

        {groups.map((group) => {
          const entries = group.entries.filter(match);
          if (entries.length === 0) return null;
          return (
            <section key={group.key} className="mb-10">
              <h2 className="mb-4 flex items-baseline gap-2 text-lg font-extrabold">
                {isRTL ? group.titleAr : group.titleEn}
                <span className="text-xs font-semibold text-ink-muted">{entries.length}</span>
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {entries.map((entry) => (
                  <div
                    key={`${group.key}-${entry.id}`}
                    className="flex items-center gap-3 rounded-2xl border border-[var(--pv-border-soft)] bg-white p-3.5"
                  >
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-purple-50 text-mq-primary">
                      <DomainIcon id={entry.id as DomainIconId} size={24} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold">{isRTL ? entry.labelAr : entry.labelEn}</span>
                      <span className="block truncate text-xs text-ink-muted">{isRTL ? entry.labelEn : entry.labelAr}</span>
                      <code className="block truncate text-[10px] text-mq-primary/70" dir="ltr">{entry.id}</code>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        {uncatalogued.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-4 text-lg font-extrabold text-amber-700">
              {isRTL ? 'في السجل بدون تصنيف' : 'In registry, uncatalogued'}
            </h2>
            <div className="flex flex-wrap gap-2">
              {uncatalogued.map((id) => (
                <span key={id} className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">
                  <DomainIcon id={id} size={16} />
                  <code dir="ltr">{id}</code>
                </span>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
